/**
 * OutreachRowPills — the Outreach cell (owner sign-off 2026-10-15; Figma 505:49931): one line of pills, the state, the agreed
 * amount once there is one, and at most one badge when the admin must act. The muted "next step / why not" line is its own
 * component so the inbox can set it under the time. rowLabels decides every word; this only draws them. Hover text sits in a
 * focusable tooltip; the Stopped pill also carries its reason as screen-reader text, because a tap never opens a tooltip.
 */
import { memo, type ReactNode } from 'react';
import type { OutreachRow } from '@discovr/types';
import { cn } from '../../../lib/utils';
import { PILL_BASE } from './OutreachStatusPill';
import { PILL_COLORS, PILL_GREEN } from './pillPalette';
import { CONTACTED_PILL_LABEL, rowLabels, waitingSendHint } from './rowLabels';
import { FOCUS_RING, StatusTooltip } from './StatusTooltip';

export { waitingSendHint };

function Hovered({ hint, testId, className, children }: { hint?: string; testId: string; className: string; children: ReactNode }) {
  if (!hint) return <span data-testid={testId} className={className}>{children}</span>;
  return (
    <StatusTooltip content={hint}>
      <span data-testid={testId} tabIndex={0} className={cn(className, FOCUS_RING)}>{children}</span>
    </StatusTooltip>
  );
}

interface RowLabelProps {
  row: OutreachRow;
  campaignZone?: string;
  /** The campaign's Automatic follow-ups switch; OFF hides the automatic-step countdown. */
  autoFollowups?: boolean;
}

function OutreachRowPillsView({
  row,
  campaignZone,
  autoFollowups = true,
  hideContactedPill = false,
  nameStopReason = false,
}: RowLabelProps & {
  /** The Contacted tab already says "Contacted", so its rows drop the word; the badge stays. */
  hideContactedPill?: boolean;
  /** The thread header names why a row stopped ("Stopped · Ghosted"); the inbox keeps the bare word with the reason on hover. */
  nameStopReason?: boolean;
}) {
  const { pill, amount, badge, doNotContact } = rowLabels(row, { campaignZone, autoFollowups });
  const named = nameStopReason ? pill.stopReason : undefined;
  const showPill = !(hideContactedPill && pill.label === CONTACTED_PILL_LABEL);
  if (!showPill && !amount && !badge && !doNotContact) return null;
  return (
    <div className="flex flex-wrap items-center justify-end gap-1.5">
      {showPill && (
        <Hovered hint={pill.hint} testId="row-pill" className={cn(PILL_BASE, PILL_COLORS[pill.tone])}>
          {named ? `${pill.label} · ${named}` : pill.label}
          {pill.hint && !named && <span className="sr-only">{` · ${pill.hint}`}</span>}
        </Hovered>
      )}
      {amount && <span data-testid="row-amount" className={cn(PILL_BASE, PILL_GREEN)}>{amount}</span>}
      {badge && <Hovered hint={badge.hint} testId="row-badge" className={cn(PILL_BASE, PILL_COLORS[badge.tone])}>{badge.label}{badge.hint && <span className="sr-only">{` · ${badge.hint}`}</span>}</Hovered>}
      {doNotContact && <Hovered hint={doNotContact.hint} testId="row-dnc" className={cn(PILL_BASE, PILL_COLORS[doNotContact.tone])}>{doNotContact.label}{doNotContact.hint && <span className="sr-only">{` · ${doNotContact.hint}`}</span>}</Hovered>}
    </div>
  );
}

/** Memoised: rowLabels walks the whole row, and a table re-renders every cell on each interaction. */
export const OutreachRowPills = memo(OutreachRowPillsView);

function OutreachRowLineView({ row, campaignZone, autoFollowups = true, className }: RowLabelProps & { className?: string }) {
  const { line } = rowLabels(row, { campaignZone, autoFollowups });
  if (!line) return null;
  const cls = cn('block truncate text-xs text-muted-foreground', className);
  // The time column is narrow: a line with no hover text of its own still shows in full on hover.
  if (!line.hint) return <span data-testid="row-line" title={line.text} className={cls}>{line.text}</span>;
  return <Hovered hint={`${line.text} · ${line.hint}`} testId="row-line" className={cls}>{line.text}</Hovered>;
}

export const OutreachRowLine = memo(OutreachRowLineView);
